import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../components/layout/Navbar';
import Footer from '../../../components/layout/Footer';
import { Card } from '../../../components/ui/card';
import { Progress } from '../../../components/ui/progress';
import { Button } from '../../../components/ui/button';
import { Award, BookOpen, TrendingUp, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useLearnAuth } from '@/hooks/useLearnAuth';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import CertificateCard from '@/components/learn/CertificateCard';

interface ModuleProgress {
  module_id: string;
  completed: boolean;
  quiz_score: number | null;
  module_slug: string;
  module_title: string;
  module_order: number;
}

const LearnDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = useLearnAuth();
  const [moduleProgress, setModuleProgress] = useState<ModuleProgress[]>([]);
  const [certificates, setCertificates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const handleLogout = async () => {
    await signOut();
    navigate('/learn/auth');
  };

  useEffect(() => {
    if (!user) return;

    const fetchProgress = async () => {
      try {
        // Fetch all modules
        const { data: modules } = await supabase
          .from('modules')
          .select('id, slug, title, module_order')
          .order('module_order');

        // Fetch user progress
        const { data: progress } = await supabase
          .from('quiz_attempts')
          .select('module_id, score, passed')
          .eq('user_id', user.id);

        // Combine data
        const progressMap = new Map();
        progress?.forEach((p) => {
          if (!progressMap.has(p.module_id) || p.score > (progressMap.get(p.module_id)?.score || 0)) {
            progressMap.set(p.module_id, p);
          }
        });

        const combined = modules?.map((m) => ({
          module_id: m.id,
          completed: progressMap.get(m.id)?.passed || false,
          quiz_score: progressMap.get(m.id)?.score || null,
          module_slug: m.slug,
          module_title: m.title,
          module_order: m.module_order,
        })) || [];

        setModuleProgress(combined);

        // Fetch certificates
        const { data: certsData, error: certsError } = await supabase
          .from('certificates')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'issued')
          .order('issue_date', { ascending: false });

        if (certsError) {
          console.error('Error fetching certificates:', certsError);
        } else {
          setCertificates(certsData || []);
        }
      } catch (error) {
        console.error('Error fetching progress:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, [user]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const completedCount = moduleProgress.filter((m) => m.completed).length;
  const totalModules = moduleProgress.length;
  const overallProgress = totalModules > 0 ? Math.round((completedCount / totalModules) * 100) : 0;

  const nextModule = moduleProgress.find((m) => !m.completed);
  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-brand-navy">My Learning Dashboard</h1>
            <Button variant="outline" onClick={handleLogout}>Logout</Button>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Progress Card */}
            <Card className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Overall Progress</p>
                  <p className="text-3xl font-bold text-brand-navy">{overallProgress}%</p>
                </div>
                <BookOpen className="w-10 h-10 text-brand-teal" />
              </div>
              <Progress value={overallProgress} className="mb-2" />
              <p className="text-xs text-gray-600">{completedCount} of {totalModules} modules completed</p>
            </Card>

            {/* Streak Card */}
            <Card className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Modules Started</p>
                  <p className="text-3xl font-bold text-brand-navy">{moduleProgress.filter(m => m.quiz_score !== null).length}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-brand-teal" />
              </div>
              <p className="text-sm text-gray-700">Keep learning!</p>
            </Card>

            {/* Certificate Status */}
            <Card className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Certificate Status</p>
                  <p className="text-lg font-bold text-gray-800">
                    {completedCount === totalModules ? 'Ready for Capstones' : 'In Progress'}
                  </p>
                </div>
                <Award className={`w-10 h-10 ${completedCount === totalModules ? 'text-brand-teal' : 'text-gray-400'}`} />
              </div>
              <p className="text-xs text-gray-600">
                {completedCount === totalModules 
                  ? '2 capstones to complete' 
                  : `${totalModules - completedCount} modules remaining`}
              </p>
            </Card>
          </div>

          {/* Next Steps */}
          {nextModule && (
            <Card className="p-6 mb-8">
              <h2 className="text-xl font-semibold mb-4 text-brand-navy">Next Steps</h2>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-brand-teal/10 rounded-full flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-brand-teal" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 mb-2">
                    Continue: Module {nextModule.module_order} - {nextModule.module_title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Continue your learning journey with the next module.
                  </p>
                  <Button size="sm" onClick={() => navigate(`/learn/app/modules/${nextModule.module_slug}`)}>
                    Continue Learning
                  </Button>
                </div>
              </div>
            </Card>
          )}
          
          {completedCount === totalModules && (
            <Card className="p-6 mb-8 bg-brand-teal/5 border-brand-teal">
              <h2 className="text-xl font-semibold mb-4 text-brand-navy">🎉 Ready for Capstones!</h2>
              <p className="text-gray-700 mb-4">
                You've completed all modules! Now complete your capstone projects to earn your certificate.
              </p>
              <div className="flex gap-3">
                <Button onClick={() => navigate('/learn/app/capstone/roi')}>
                  ROI Calculator Capstone
                </Button>
                <Button variant="outline" onClick={() => navigate('/learn/app/capstone/privacy')}>
                  Privacy Checklist Capstone
                </Button>
              </div>
            </Card>
          )}

          {/* Certificates Section */}
          <Card className="p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-brand-navy">
              <Award className="w-5 h-5" />
              Your Certificates
            </h2>
            {certificates.length > 0 ? (
              <div className="space-y-4">
                {certificates.map((cert) => (
                  <CertificateCard key={cert.id} certificate={cert} />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">Foundation Certificate</h3>
                    <Award className="h-5 w-5 text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Complete modules 1-4 to earn this certificate
                  </p>
                  <Progress value={completedCount >= 4 ? 100 : (completedCount / 4) * 100} />
                </div>
                <div className="p-4 border rounded-lg opacity-60">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">Full Program Certificate</h3>
                    <Award className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Complete all 16 modules and capstone project to earn this certificate
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* Module Progress List */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4 text-brand-navy">Your Progress</h2>
            {moduleProgress.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No modules available yet.</p>
            ) : (
              <div className="space-y-3">
                {moduleProgress.map((module) => (
                  <div
                    key={module.module_id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/learn/app/modules/${module.module_slug}`)}
                  >
                    {module.completed ? (
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">
                        Module {module.module_order}: {module.module_title}
                      </p>
                      {module.quiz_score !== null && (
                        <p className="text-sm text-gray-600">
                          Quiz score: {module.quiz_score}%
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default LearnDashboard;
